import apiService from './api';
import { 
  CreatePaymentIntentRequest, 
  PaymentIntentResponse, 
  TransactionStatus,
  DepositTransaction 
} from '../types/payment';

interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
  timestamp: string;
  path: string;
}

interface GetTransactionsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: TransactionStatus;
  dateRange?: 'all' | '7d' | '30d' | '90d';
}

interface TransactionsResponse {
  deposits: DepositTransaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface MonthlyStatsResponse {
  currentMonth: {
    totalAmount: number;
    totalDeposits: number;
    totalEpoints: number;
    avgDepositAmount: number;
  };
  previousMonth: {
    totalAmount: number;
    totalDeposits: number;
    totalEpoints: number;
    avgDepositAmount: number;
  };
  growth: {
    amountGrowthPercent: number;
    depositsGrowthPercent: number;
    epointsGrowthPercent: number;
  };
  lastTransactionDate?: string;
}

export class DepositsApiService {
  async createPaymentIntent(data: CreatePaymentIntentRequest): Promise<PaymentIntentResponse> {
    const response = await apiService.post<ApiResponse<PaymentIntentResponse>>('/deposits/create-intent', data);
    return response.data;
  }

  async confirmDeposit(depositId: string, paymentIntentId: string) {
    const response = await apiService.post(`/deposits/${depositId}/confirm`, {
      paymentIntentId
    });
    return response;
  }

  async getTransactions(params: GetTransactionsParams = {}): Promise<TransactionsResponse> {
    const response = await apiService.get<any>('/deposits/history', params);
    // Some environments wrap data twice: { data: { data: { deposits, pagination } } }
    const payload = response?.data?.data ?? response?.data ?? response;
    return payload as TransactionsResponse;
  }

  async getTransactionById(id: string): Promise<DepositTransaction> {
    const response = await apiService.get<{ data: DepositTransaction }>(`/deposits/transactions/${id}`);
    return response.data;
  }

  async getDepositDetails(depositId: string) {
    const response = await apiService.get<{ 
      statusCode: number; 
      message: string; 
      data: any; 
      timestamp: string; 
      path: string 
    }>(`/deposits/${depositId}`);
    return response.data.data;
  }

  async getMonthlyStats(): Promise<MonthlyStatsResponse> {
    const response = await apiService.get<{
      statusCode: number;
      message: string;
      data: MonthlyStatsResponse;
      timestamp: string;
      path: string;
    }>('/deposits/monthly-stats');
    return response.data;
  }

  async getDepositLimits() {
    const response = await apiService.get<{
      data: {
        daily: {
          limit: number;
          used: number;
          remaining: number;
          resetTime: string;
        };
        transaction: {
          min: number;
          max: number;
        };
      }
    }>('/deposits/limits');
    return response.data;
  }

  async getConversionRates() {
    const response = await apiService.get<{
      data: {
        eurToEpoints: number;
        minDepositEur: number;
        maxDepositEur: number;
        supportedCurrencies: string[];
        lastUpdated: string;
      }
    }>('/deposits/rates');
    return response.data;
  }
}

export const depositsApi = new DepositsApiService();
export default depositsApi;